import { useMemo } from 'react';
import type { TreeConditionNode } from '@/components/tree-dnd/tree-dnd-types';
import { ConditionEditSheet } from '@/components/tree-dnd/tree-dnd-conditions/condition-edit-sheet';
import { ConditionEditSheetTargetFooter } from '@/components/tree-dnd/tree-dnd-conditions/condition-edit-sheet-target-footer';
import { ConditionField } from '@/components/tree-dnd/tree-dnd-conditions/condition-field';
import { ConditionNodeSummary } from '@/components/tree-dnd/tree-dnd-conditions/condition-node-summary';
import { DateFilterControl } from '@/components/tree-dnd/tree-dnd-conditions/date-filter-control';
import { uncapitalize } from '@/components/tree-dnd/tree-dnd-conditions/condition-summary-format';
import { useConditionAudiencePreview } from '@/components/tree-dnd/tree-dnd-conditions/use-condition-audience-preview';
import { useDateFilterSummary } from '@/components/tree-dnd/tree-dnd-conditions/use-condition-summaries';
import { useConditionEditSheet } from '@/components/tree-dnd/use-condition-edit-sheet';
import { useI18nContext } from '@/i18n/i18n-react';
import type { Locales } from '@/i18n/i18n-types';

interface LastNotificationAtConditionProps {
  node: Extract<TreeConditionNode, { kind: 'lastNotificationAt' }>;
  onChange: (
    value: Extract<TreeConditionNode, { kind: 'lastNotificationAt' }>['args']
  ) => void;
}

export function LastNotificationAtCondition({
  node,
  onChange,
}: LastNotificationAtConditionProps) {
  const { LL, locale } = useI18nContext();
  const { open, onOpenChange } = useConditionEditSheet(node.id);
  const value = node.args;
  const summaryDetail = useDateFilterSummary(value.when);
  const conditionLabel = LL.audiences.filters.conditions.lastNotificationAt();

  const filterSummary = useMemo(() => {
    const when = uncapitalize(summaryDetail);
    if (node.negated) {
      return LL.audiences.filters.conditionSummary.lastNotificationAtExcludeSummary(
        { when }
      );
    }
    return LL.audiences.filters.conditionSummary.lastNotificationAtIncludeSummary(
      { when }
    );
  }, [LL, node.negated, summaryDetail]);

  const preview = useConditionAudiencePreview(node, open);

  return (
    <>
      <ConditionNodeSummary label={conditionLabel} detail={summaryDetail} />
      <ConditionEditSheet
        open={open}
        onOpenChange={onOpenChange}
        title={conditionLabel}
        description={LL.audiences.filters.conditionSummary.lastNotificationAtDescription()}
        footerContent={
          <ConditionEditSheetTargetFooter
            summary={filterSummary}
            targetedUserCount={
              preview.isError ? undefined : preview.targetedUserCount
            }
            totalUserCount={
              preview.isError ? undefined : preview.totalUserCount
            }
            isLoading={preview.isLoading}
            locale={locale as Locales}
          />
        }
      >
        <div className="flex flex-col gap-6">
          <ConditionField
            label={LL.audiences.filters.editSheet.whenLabel()}
            helpText={LL.audiences.filters.editSheet.lastNotificationAtWhenHelp()}
          >
            <DateFilterControl
              value={value.when}
              onChange={(when) => onChange({ ...value, when })}
            />
          </ConditionField>
        </div>
      </ConditionEditSheet>
    </>
  );
}
